import React, { useContext, useEffect, useMemo } from 'react';
import { useWaitingListAttachmentFlow } from 'components/attachments';
import { useAttachmentStore } from 'stores/attachmentStore';
import { Banner, BottomButtonsLayout, Button, Meta, theme } from 'aimo-ui';
import InfoBox from 'components/common/InfoBox';
import Icon from 'components/icons/Icon';
import { TitleCard } from 'components/longTermParking';
import SvgImage from 'components/svgImage/SvgImage';
import AccountContext from 'context/AccountContext';
import usePermitPurchaseInfo from 'hooks/usePermitPurchaseInfo';
import { useTranslation } from 'react-i18next';
import { usePermitStore } from 'stores/permitStore';
import { useUiStore } from 'stores/uiStore';
import { SimpleFullContainer, StyledWrapper } from 'styles/ContainerStyles';
import { CountryCode } from 'types/generatedSchemaTypes';
import {
  isRequiredSalesOrderAttachmentMissing,
  resolveAttachmentUi,
} from 'utils/attachmentUtils';
import { isWeb } from 'utils/deviceUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useJoinWaitingList } from 'hooks/useJoinWaitingList';

const WarningIcon = () => <Icon name="Info-Outlined" />;

const WaitingListJoinScreen: React.FC = () => {
  const { t } = useTranslation();
  const { customer } = useContext(AccountContext);
  const { placeName, price, selectedPmc, selectedBillingFrequency } =
    usePermitPurchaseInfo();
  const { name, pmcId, countryCode, attachmentPolicy } = selectedPmc ?? {};
  const { showOnWaitingList, requireOnWaitingList } =
    resolveAttachmentUi(attachmentPolicy);

  const waitingListAttachmentFlow = useWaitingListAttachmentFlow();

  useEffect(() => {
    if (!pmcId || !showOnWaitingList) {
      return;
    }
    const { setAttachmentContext, setSelectedAttachment, setAttachmentFlow } =
      useAttachmentStore.getState();
    setAttachmentContext(pmcId, waitingListAttachmentFlow, false);
    setSelectedAttachment(null);
    return () => {
      setAttachmentFlow(null);
    };
  }, [pmcId, waitingListAttachmentFlow, showOnWaitingList]);

  const billingInterval = usePermitStore(
    (state) => state.selectedPermitBillingInterval
  );
  const hasSelectedBillingInterval = Boolean(
    billingInterval?.selectedBillingFrequency
  );

  const liveAttachments = useAttachmentStore((state) => state.attachments);
  const uploadedAttachmentCount = useMemo(
    () =>
      pmcId
        ? liveAttachments.filter(
            (attachment) =>
              attachment.contextId === pmcId && !!attachment.uploadUid
          ).length
        : 0,
    [liveAttachments, pmcId]
  );
  const requiredAttachmentMissing = isRequiredSalesOrderAttachmentMissing(
    requireOnWaitingList,
    uploadedAttachmentCount
  );

  const { joinWaitingList, loading } = useJoinWaitingList();

  const handleJoinWaitingList = () => {
    if (requiredAttachmentMissing) {
      useUiStore.getState().setSheetModal('addAttachment');
      return;
    }
    joinWaitingList({
      pmcId: pmcId ?? '',
      countryCode: countryCode as CountryCode,
      customerEmail: customer.contactEmail ?? '',
      billingInterval: selectedBillingFrequency ?? '',
      includeAttachments: showOnWaitingList,
    });
  };

  const joinButtonText =
    requiredAttachmentMissing && !isWeb
      ? t('longTermParking.attachment.addAttachment')
      : t('longTermParking.waitingList.title');

  return (
    <BottomButtonsLayout
      contentBottom={
        <>
          <InfoBox
            padding={15}
            margin={20}
            bgColor={theme.colors.lightBg.green}
            leftComponent={
              <SvgImage size="l" name="Lamp" testID={'pricing-info'} />
            }>
            <SimpleFullContainer>
              <Meta>{t('longTermParking.waitingList.join.payment')}</Meta>
            </SimpleFullContainer>
          </InfoBox>
          {isWeb && requireOnWaitingList && (
            <StyledWrapper mt={10}>
              <Banner
                text={t('longTermParking.attachment.webNotSupported')}
                type="warning"
                iconComponent={WarningIcon}
              />
            </StyledWrapper>
          )}
          <Button
            size="l"
            text={joinButtonText}
            onPress={handleJoinWaitingList}
            disabled={
              !hasSelectedBillingInterval ||
              loading ||
              (isWeb && requiredAttachmentMissing)
            }
            loading={loading}
            {...testIdentifiers('waiting-list-join-button')}
          />
        </>
      }>
      <TitleCard
        parkingZoneName={placeName}
        permitName={name ?? ''}
        price={price}
        onlineBookingFlexibleStartDate={0}
        enableDateTimePicker={false}
        showAttachment={showOnWaitingList}
      />
    </BottomButtonsLayout>
  );
};

export default WaitingListJoinScreen;
